import { useEnvOptional } from './EnvContext';
import { useCurrentUser } from '../auth/useCurrentUser';

/**
 * ENV-LEVEL editability for the currently-selected env.
 *
 * dev01 is the SINGLE editable env on EVERY domain (EnvContext.editableEnv is
 * always dev01 — no per-deploy variance, no persistence; see EnvContext). So
 * editability is a pure client decision: `currentEnv === editableEnv`.
 *
 * We deliberately do NOT consult /api/app-env here anymore:
 *   - `appEnv.editableEnv` is the SERVING deployment's env (e.g. `qa` on the qa
 *     portal). The old `currentEnv === appEnv.editableEnv` compare therefore
 *     gated DEV01 as read-only on dosa.qa/uat even though dev01 IS the editable
 *     env — the "still read-only with dev01 selected" bug.
 *   - the retired per-tenant editing-env flag ('any non-prod is editable') is
 *     likewise gone: only dev01 is editable, and writes to it route
 *     browser-direct to the dev01 backend, whose fence is the authoritative gate.
 *
 * Invariants:
 *   - Outside an EnvProvider (tenant picker, leaf component / unit test) → true
 *     (transparent; the server write-fence is the real gate).
 *   - No async dependency: editableEnv is build-time and currentEnv defaults to
 *     it, so this is deterministic from first render (no fail-closed flicker).
 */
export const useIsEditable = (): boolean => {
  const env = useEnvOptional();
  const { hasRole, isSuperAdmin, rbacV2Enabled } = useCurrentUser();
  // Super Editor & Publisher (mig 271): may edit + publish on ANY env. The
  // server grants the matching env-fence bypass, and the axios baseURL already
  // follows the SELECTED env, so writes land on whatever env is in view. Once
  // /api/me is cached hasRole is synchronous; before that it is false, so
  // non-holders and the pre-load frame behave exactly as before (env check).
  // RBAC_V2 (decisions §10): super-editor-publisher folds into Super Admin —
  // every super-admin gets the bypass, not just the literal SEP role holder.
  if (rbacV2Enabled ? isSuperAdmin : hasRole('super-editor-publisher')) return true;
  // Outside an EnvProvider: transparent (let the server decide).
  if (!env) return true;
  return env.currentEnv === env.editableEnv;
};

export default useIsEditable;
